// src/services/groupService.ts
import { logger } from '../utils/logger';



import { supabase } from './supabase';
import { moderateChatMessage } from './contentModeration';



export interface Group {
  id: string;
  name: string;
  description: string | null;
  creator_id: string;
  group_type: 'private' | 'public';
  game_focus: string | null;
  avatar_url: string | null;
  max_members: number;
  is_quest_group: boolean;
  created_at: string;
  updated_at: string;
}



export interface GroupMember {

  id: string;

  group_id: string;

  user_id: string;

  role: 'creator' | 'admin' | 'member';

  joined_at: string;

  profile?: {

    username: string;

    display_name: string;

    avatar_url: string | null;

    avatar: any;

    is_admin?: boolean;

  };

}



export interface GroupMessage {

  id: string;

  group_id: string;

  sender_id: string;

  content: string;

  created_at: string;

  sender?: {

    id: string;

    username: string;

    display_name: string;

    avatar_url: string | null;

    avatar: any;

    is_admin?: boolean;

  };

}



export interface GroupWithDetails extends Group {

  member_count: number;

  members?: GroupMember[];

  last_message?: GroupMessage;

}



/**

 * Create a new group

 */

export async function createGroup(

  name: string,

  memberIds: string[],

  description?: string,

  imagesEnabled?: boolean,

  gameFocus?: string

): Promise<{ success: boolean; group?: Group; error?: string }> {

  try {

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {

      return { success: false, error: 'Not authenticated' };

    }



    logger.log('ðŸŽ® Creating group:', { name, memberIds, description, imagesEnabled, gameFocus });



    // 1. Create the group

    const { data: group, error: groupError } = await supabase

      .from('groups')

      .insert({

        name: name.trim(),

        description: description?.trim() || null,

        creator_id: user.id,

        group_type: 'private',

        images_enabled: imagesEnabled || false,

        game_focus: gameFocus || null

      })

      .select()

      .single();



    if (groupError) {

      logger.error('âŒ Error creating group:', groupError);

      return { success: false, error: groupError.message };

    }



    logger.log('âœ… Group created:', group.id);



    // 2. EXPLICITLY add creator as creator role (don't rely on trigger)

    const { error: creatorError } = await supabase

      .from('group_members')

      .insert({

        group_id: group.id,

        user_id: user.id,

        role: 'creator'

      });



    if (creatorError) {

      logger.error('âŒ Error adding creator to group:', creatorError);

      logger.log('âš ï¸ Continuing despite creator error (trigger may have handled it)');

    } else {

      logger.log('âœ… Creator added to group');

    }



    // 3. Add selected members

    if (memberIds.length > 0) {

      logger.log('ðŸ‘¥ Adding members:', memberIds);



      const membersToAdd = memberIds.map(userId => ({

        group_id: group.id,

        user_id: userId,

        role: 'member' as const

      }));



      const { data: addedMembers, error: membersError } = await supabase

        .from('group_members')

        .insert(membersToAdd)

        .select();



      if (membersError) {

        logger.error('âŒ Error adding members:', membersError);

        return {

          success: true,

          group,

          error: `Group created but some members couldn't be added: ${membersError.message}`

        };

      }



      logger.log(`âœ… Added ${addedMembers?.length || 0} members successfully`);

    }



    // 4. Verify final member count

    const { count } = await supabase

      .from('group_members')

      .select('*', { count: 'exact', head: true })

      .eq('group_id', group.id);



    logger.log(`âœ… Final member count: ${count}`);



    return { success: true, group };

  } catch (error) {

    logger.error('ðŸ’¥ Exception in createGroup:', error);

    return { success: false, error: 'Failed to create group' };

  }

}



/**

 * Get all groups the current user is a member of

 */

export async function getMyGroups(): Promise<{

  success: boolean;

  groups?: GroupWithDetails[];

  error?: string

}> {

  try {

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {

      return { success: false, error: 'Not authenticated' };

    }



    const { data: groupMembers, error: membersError } = await supabase

      .from('group_members')

      .select('group_id')

      .eq('user_id', user.id);



    if (membersError) {

      logger.error('Error fetching group memberships:', membersError);

      return { success: false, error: membersError.message };

    }



    if (!groupMembers || groupMembers.length === 0) {

      return { success: true, groups: [] };

    }



    const groupIds = groupMembers.map(gm => gm.group_id);



    const { data: groups, error: groupsError } = await supabase

      .from('groups')

      .select('*')
      .in('id', groupIds)
      .eq('is_quest_group', false) // Exclude Pip's Guild Quest groups from general chat list
      .order('updated_at', { ascending: false });



    if (groupsError) {

      logger.error('Error fetching groups:', groupsError);

      return { success: false, error: groupsError.message };

    }



    const groupsWithDetails: GroupWithDetails[] = await Promise.all(

      (groups || []).map(async (group) => {

        const { count } = await supabase

          .from('group_members')

          .select('*', { count: 'exact', head: true })

          .eq('group_id', group.id);



        const { data: lastMessage } = await supabase

          .from('group_messages')

          .select('*')

          .eq('group_id', group.id)

          .order('created_at', { ascending: false })

          .limit(1)

          .maybeSingle();



        if (lastMessage) {

          // FIXED: Add is_admin to sender profile query

          const { data: sender } = await supabase

            .from('profiles')

            .select('id, username, display_name, avatar_url, meeple_avatar, is_admin')

            .eq('id', lastMessage.sender_id)

            .single();



          if (sender) {

            // Map meeple_avatar to avatar for application use

            (sender as any).avatar = sender.meeple_avatar;

            lastMessage.sender = sender;

          }

        }



        return {

          ...group,

          member_count: count || 0,

          last_message: lastMessage || undefined

        };

      })

    );



    return { success: true, groups: groupsWithDetails };

  } catch (error) {

    logger.error('Error in getMyGroups:', error);

    return { success: false, error: 'Failed to fetch groups' };

  }

}



/**

 * Get detailed information about a specific group

 */

export async function getGroupDetails(

  groupId: string

): Promise<{ success: boolean; group?: GroupWithDetails; error?: string }> {

  try {

    const { data: group, error: groupError } = await supabase

      .from('groups')

      .select('*')

      .eq('id', groupId)

      .single();



    if (groupError) {

      logger.error('Error fetching group:', groupError);

      return { success: false, error: groupError.message };

    }



    const { data: memberRecords, error: membersError } = await supabase

      .from('group_members')

      .select('*')

      .eq('group_id', groupId);



    if (membersError) {

      logger.error('Error fetching members:', membersError);

    }



    const members = await Promise.all(

      (memberRecords || []).map(async (member) => {

        // FIXED: Add is_admin to profile query

        const { data: profile } = await supabase

          .from('profiles')

          .select('username, display_name, avatar_url, meeple_avatar, is_admin')

          .eq('id', member.user_id)

          .single();



        // Map meeple_avatar to avatar for application use

        if (profile) {

          (profile as any).avatar = profile.meeple_avatar;

        }



        return {

          ...member,

          profile: profile || undefined

        };

      })

    );



    return {

      success: true,

      group: {

        ...group,

        member_count: members?.length || 0,

        members: members || []

      }

    };

  } catch (error) {

    logger.error('Error in getGroupDetails:', error);

    return { success: false, error: 'Failed to fetch group details' };

  }

}



/**

 * Add a member to a group

 */

export async function addMemberToGroup(

  groupId: string,

  userId: string

): Promise<{ success: boolean; error?: string }> {

  try {

    const { error } = await supabase

      .from('group_members')

      .insert({

        group_id: groupId,

        user_id: userId,

        role: 'member'

      });



    if (error) {

      logger.error('Error adding member:', error);

      return { success: false, error: error.message };

    }



    return { success: true };

  } catch (error) {

    logger.error('Error in addMemberToGroup:', error);

    return { success: false, error: 'Failed to add member' };

  }

}



/**

 * Remove a member from a group

 */

export async function removeMemberFromGroup(

  groupId: string,

  userId: string

): Promise<{ success: boolean; error?: string }> {

  try {

    const { error } = await supabase

      .from('group_members')

      .delete()

      .eq('group_id', groupId)

      .eq('user_id', userId);



    if (error) {

      logger.error('Error removing member:', error);

      return { success: false, error: error.message };

    }



    return { success: true };

  } catch (error) {

    logger.error('Error in removeMemberFromGroup:', error);

    return { success: false, error: 'Failed to remove member' };

  }

}



/**

 * Leave a group

 */

export async function leaveGroup(

  groupId: string

): Promise<{ success: boolean; error?: string }> {

  try {

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {

      return { success: false, error: 'Not authenticated' };

    }



    return removeMemberFromGroup(groupId, user.id);

  } catch (error) {

    logger.error('Error in leaveGroup:', error);

    return { success: false, error: 'Failed to leave group' };

  }

}



/**

 * Send a message to a group

 */

export async function sendGroupMessage(

  groupId: string,

  content: string

): Promise<{ success: boolean; message?: GroupMessage; error?: string }> {

  try {

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {

      return { success: false, error: 'Not authenticated' };

    }

    // Content moderation - block hate speech and slurs
    const moderationCheck = moderateChatMessage(content);
    if (!moderationCheck.isClean) {
      return {
        success: false,
        error: 'Message contains inappropriate content. This platform does not tolerate hate speech or slurs.'
      };
    }



    logger.log('ðŸ“¤ START: Sending group message');

    logger.log('  Group ID:', groupId);

    logger.log('  User ID:', user.id);

    logger.log('  Content length:', content.length);



    // 1. Save message to database

    const { data: message, error: messageError } = await supabase

      .from('group_messages')

      .insert({

        group_id: groupId,

        sender_id: user.id,

        content: content.trim()

      })

      .select()

      .single();



    if (messageError) {

      logger.error('âŒ Error sending group message:', messageError);

      return { success: false, error: messageError.message };

    }



    logger.log('âœ… Message saved:', message.id);



    // 2. Fetch sender profile - FIXED: Add is_admin

    const { data: sender } = await supabase

      .from('profiles')

      .select('id, username, display_name, avatar_url, meeple_avatar, is_admin')

      .eq('id', user.id)

      .single();



    if (sender) {

      // Map meeple_avatar to avatar for application use

      (sender as any).avatar = sender.meeple_avatar;

      message.sender = sender;

    }



    logger.log('âœ… Sender profile loaded:', sender?.username);



    // 3. Update group's updated_at timestamp

    await supabase

      .from('groups')

      .update({ updated_at: new Date().toISOString() })

      .eq('id', groupId);



    // 4. Get group details for notification

    const { data: group } = await supabase

      .from('groups')

      .select('name')

      .eq('id', groupId)

      .single();



    logger.log('âœ… Group loaded:', group?.name);



    // 5. Get all group members except sender

    const { data: members, error: membersError } = await supabase

      .from('group_members')

      .select('user_id')

      .eq('group_id', groupId)

      .neq('user_id', user.id);



    if (membersError) {

      logger.error('âŒ Error loading members:', membersError);

    }



    logger.log(`âœ… Found ${members?.length || 0} members to notify`);



    // 6. Send push notifications to all members

    if (members && members.length > 0) {

      logger.log('ðŸ”” STARTING NOTIFICATION PROCESS');



      for (const member of members) {

        logger.log(`ðŸ“¨ Invoking function for member: ${member.user_id}`);



        try {

          const notificationPayload = {

            type: 'new_group_message',

            recipientId: member.user_id,

            senderId: user.id,

            senderName: sender?.display_name || sender?.username || 'Someone',

            groupId: groupId,

            groupName: group?.name || 'Group Chat',

            messagePreview: content.substring(0, 100),

            messageId: message.id

          };



          logger.log('ðŸ“¦ Notification payload:', JSON.stringify(notificationPayload, null, 2));



          const { data: invokeData, error: invokeError } = await supabase.functions.invoke('send-notification', {

            body: notificationPayload

          });



          if (invokeError) {

            logger.error(`âŒ Invoke error for ${member.user_id}:`, invokeError);

          } else {

            logger.log(`âœ… Invoke success for ${member.user_id}:`, invokeData);

          }

        } catch (error) {

          logger.error(`ðŸ’¥ Exception invoking for ${member.user_id}:`, error);

        }

      }



      logger.log('ðŸ”” NOTIFICATION PROCESS COMPLETE');

    } else {

      logger.log('â„¹ï¸ No other members to notify');

    }



    logger.log('âœ… END: Group message sent successfully');

    return { success: true, message };

  } catch (error) {

    logger.error('ðŸ’¥ FATAL ERROR in sendGroupMessage:', error);

    return { success: false, error: 'Failed to send message' };

  }

}



const GROUP_MESSAGE_PAGE_SIZE = 50;

/**
 * Get messages for a group with cursor-based pagination.
 * Pass `before` (a created_at ISO string) to load messages older than that cursor.
 * Uses a single join query — no N+1 profile fetches.
 */
export async function getGroupMessages(
  groupId: string,
  before?: string
): Promise<{ success: boolean; messages?: GroupMessage[]; hasMore?: boolean; error?: string }> {
  try {
    let query = supabase
      .from('group_messages')
      .select('*, sender:profiles!sender_id(id, username, display_name, avatar_url, meeple_avatar, is_admin)')
      .eq('group_id', groupId)
      .order('created_at', { ascending: false })
      .limit(GROUP_MESSAGE_PAGE_SIZE + 1);

    if (before) {
      query = query.lt('created_at', before);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error fetching group messages:', error);
      return { success: false, error: error.message };
    }

    const hasMore = (data || []).length > GROUP_MESSAGE_PAGE_SIZE;
    const page = hasMore ? data!.slice(0, GROUP_MESSAGE_PAGE_SIZE) : (data || []);

    // Reverse to chronological order; remap meeple_avatar → avatar
    const messages: GroupMessage[] = page.reverse().map((msg) => ({
      ...msg,
      sender: msg.sender
        ? { ...msg.sender, avatar: (msg.sender as any).meeple_avatar }
        : undefined,
    }));

    return { success: true, messages, hasMore };
  } catch (error) {
    logger.error('Error in getGroupMessages:', error);
    return { success: false, error: 'Failed to fetch messages' };
  }
}



/**

 * Subscribe to new messages in a group

 */

export function subscribeToGroupMessages(

  groupId: string,

  callback: (message: GroupMessage) => void

) {

  const subscription = supabase

    .channel(`group_messages:${groupId}`)

    .on(

      'postgres_changes',

      {

        event: 'INSERT',

        schema: 'public',

        table: 'group_messages',

        filter: `group_id=eq.${groupId}`

      },

      async (payload) => {

        // FIXED: Add is_admin to sender profile query

        const { data: sender } = await supabase

          .from('profiles')

          .select('id, username, display_name, avatar_url, meeple_avatar, is_admin')

          .eq('id', (payload.new as GroupMessage).sender_id)

          .single();



        // Map meeple_avatar to avatar for application use

        if (sender) {

          (sender as any).avatar = sender.meeple_avatar;

        }



        callback({

          ...payload.new as GroupMessage,

          sender: (sender || undefined) as any

        });

      }

    )

    .subscribe();



  return subscription;

}



/**

 * Unsubscribe from group message updates

 */

export async function unsubscribeFromGroupMessages(subscription: any) {

  await supabase.removeChannel(subscription);

}



/**

 * Delete a group (creator only)

 */

export async function deleteGroup(

  groupId: string

): Promise<{ success: boolean; error?: string }> {

  try {

    const { error } = await supabase

      .from('groups')

      .delete()

      .eq('id', groupId);



    if (error) {

      logger.error('Error deleting group:', error);

      return { success: false, error: error.message };

    }



    return { success: true };

  } catch (error) {

    logger.error('Error in deleteGroup:', error);

    return { success: false, error: 'Failed to delete group' };

  }

}